import { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { Box, Typography, Collapse, CircularProgress, Button, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import { numberWithCommas } from "../../../utils/utility";
import BusItemLayout from "./busItemLayout";
import BusTicketPdfDocument from "./busTicketPdfDocument";
import { BaseButton } from "../../common/base-button/base-button";

export default function PurchasedBusTicket({
    labels = [],
    logoUrl,
    companyName,
    ticketDate,
    origin,
    destination,
    departureTime,
    price,
    ticketNumber,
    passengers = [],
}) {
    const { t } = useTranslation();

    const [showPassengers, setShowPassengers] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfError, setPdfError] = useState(false);

    const handleDownloadPdf = async () => {
        if (isGeneratingPdf) return;

        try {
            setIsGeneratingPdf(true);
            setPdfError(false);

            const blob = await pdf(
                <BusTicketPdfDocument
                    companyName={companyName}
                    origin={origin}
                    destination={destination}
                    ticketDate={ticketDate}
                    departureTime={departureTime}
                    ticketNumber={ticketNumber}
                    price={price}
                    passengers={passengers}
                    formatPrice={numberWithCommas}
                />,
            ).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `bus-ticket-${ticketNumber || Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (error) {
            console.error("Failed to generate ticket PDF:", error);
            setPdfError(true);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const renderPassengerList = () => {
        if (!passengers.length) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}>
                    {t("noPassengerData", "اطلاعات مسافری ثبت نشده است.")}
                </Typography>
            );
        }

        return (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2, pr: 2, listStyle: "none" }}>
                {passengers.map((passenger, index) => (
                    <Grid container xs={12} key={`${passenger.last_name}-${index}`} gap={5}>
                        <Typography
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}
                        >
                            {index + 1}. {passenger.first_name} {passenger.last_name}
                        </Typography>
                        <Typography
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}
                        >
                            کدملی: {passenger.national_code}
                        </Typography>
                        <Typography
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}
                        >
                            شماره صندلی: {passenger.seat_number}
                        </Typography>
                    </Grid>
                ))}
            </Box>
        );
    };

    return (
        <BusItemLayout
            labels={labels}
            logoUrl={logoUrl}
            companyName={companyName}
            ticketDate={ticketDate}
            origin={origin}
            destination={destination}
            departureTime={departureTime}
            isCardClickable={() => false}
            renderExtraContent={() => (
                <>
                    <Button
                        size="small"
                        onClick={() => setShowPassengers(prev => !prev)}
                        aria-expanded={showPassengers}
                        endIcon={
                            <ExpandMoreIcon
                                sx={{
                                    transform: showPassengers ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                }}
                            />
                        }
                        sx={{ minWidth: 0, px: 0.5, fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.8rem" }, color: "var(--primary-color)" }}
                    >
                        {t("passengers", "مسافران")} ({passengers.length})
                    </Button>
                    <Collapse in={showPassengers}>{renderPassengerList()}</Collapse>
                </>
            )}
            renderFooter={() => (
                <>
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: "0.78rem", sm: "0.9rem", md: "1rem" }, whiteSpace: "nowrap" }}
                    >
                        {numberWithCommas(price)} {t("currency")}
                    </Typography>

                    <BaseButton
                        size="medium"
                        color="primary"
                        handleClick={handleDownloadPdf}
                        disabled={isGeneratingPdf}
                        rightIcon={isGeneratingPdf ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                        text={isGeneratingPdf ? t("generatingPdf", "در حال ساخت فایل...") : t("downloadTicket", "دانلود بلیط")}
                        radius="radius-10"
                    />

                    {pdfError && (
                        <Typography variant="caption" color="error" sx={{ fontSize: "0.65rem" }}>
                            {t("pdfError", "ساخت فایل با خطا مواجه شد.")}
                        </Typography>
                    )}
                </>
            )}
        />
    );
}
